import { Preferences } from "@capacitor/preferences";

export type UnitSystem = "imperial" | "metric";

export type RecipeWeightUnit = "g" | "oz" | "lb";

export type ProfileSettings = {
  units: UnitSystem;
  recipeWeightUnit: RecipeWeightUnit;
  dietaryPreferences: string;
  allergens: string;
};

const SETTINGS_KEY = "profile:settings";

const DEFAULT_SETTINGS: ProfileSettings = {
  units: "imperial",
  recipeWeightUnit: "oz",
  dietaryPreferences: "",
  allergens: "",
};

const normalizeSettings = (value: unknown): ProfileSettings => {
  if (!value || typeof value !== "object") {
    return { ...DEFAULT_SETTINGS };
  }

  const record = value as Partial<ProfileSettings>;

  const rawRecipeUnit = record.recipeWeightUnit;

  return {
    units: record.units === "metric" ? "metric" : "imperial",
    recipeWeightUnit:
      rawRecipeUnit === "g" || rawRecipeUnit === "lb" || rawRecipeUnit === "oz"
        ? rawRecipeUnit
        : record.units === "metric"
          ? "g"
          : "oz",
    dietaryPreferences:
      typeof record.dietaryPreferences === "string"
        ? record.dietaryPreferences
        : "",
    allergens:
      typeof record.allergens === "string" ? record.allergens : "",
  };
};

export const loadProfileSettings = async (): Promise<ProfileSettings> => {
  const { value } = await Preferences.get({ key: SETTINGS_KEY });
  if (!value) {
    return { ...DEFAULT_SETTINGS };
  }

  try {
    return normalizeSettings(JSON.parse(value));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

export const saveProfileSettings = async (
  settings: ProfileSettings,
): Promise<void> => {
  await Preferences.set({
    key: SETTINGS_KEY,
    value: JSON.stringify(settings),
  });
};
